// Word-export för granskningsöversikt, startbesked och kemikalieförteckning.
import type { Paragraph, Table } from "docx";
import { downloadWordDocument } from "@/lib/documentGenerator/exportHistory";
import { createStampForCurrentUser } from "@/lib/documentGenerator/watermark";
import { buildDocument, h1, body as p, table } from "@/lib/documentGenerator/docTheme";
import { REVISION_LIST_HEADERS, currentPhaseName } from "@/lib/documentGenerator/revisionColumns";
import { documentFooter } from "@/lib/documentGenerator/documentFooter";

export interface ReviewRowExport {
  docNumber: string;
  title: string;
  owner: string;
  statusLabel: string;
  updatedAt?: string | null;
  note?: string;
  changedBy?: string | null;
  phaseName?: string | null;
}

export const generateReviewOverviewWord = async (params: {
  rows: ReviewRowExport[];
  roleLabel: string;
  periodLabel: string;
  projectName?: string;
}) => {
  const { rows, roleLabel, periodLabel, projectName } = params;
  const documentName = "Granskningsöversikt";
  const stamp = await createStampForCurrentUser({ documentName, projectName });

  const children: (Paragraph | Table)[] = [
    p(`Roll: ${roleLabel}`),
    p(`Period: ${periodLabel}`),
    p(`Uttagen: ${new Date().toLocaleString("sv-SE")}`),
    h1("1. Dokumentstatus"),
    table(
      [
        "Dok",
        "Dokument",
        "Ägande roll",
        "Status",
        "Senast ändrad",
        REVISION_LIST_HEADERS[0],
        REVISION_LIST_HEADERS[1],
        REVISION_LIST_HEADERS[2],
      ],
      rows.map((r) => [
        r.docNumber,
        r.title,
        r.owner,
        r.statusLabel,
        r.updatedAt ? new Date(r.updatedAt).toLocaleDateString("sv-SE") : "–",
        r.changedBy || "–",
        r.phaseName || currentPhaseName(),
        r.note ?? "",
      ]),
      { columnWidths: [600, 1900, 1100, 1200, 1100, 1200, 1100, 1160] }
    ),
  ];
  const footer = await documentFooter({ docNumber: "00b", documentType: "granskningsoversikt", includeSignatures: false });

  const doc = buildDocument({ meta: {
      status: footer.status,
      version: footer.version,
      review: footer.review,
      documentName, projectName }, stamp, children });
  await downloadWordDocument(doc, `Granskningsoversikt_${roleLabel.replace(/\s+/g, "_")}.docx`, {
    documentType: "granskningsoversikt",
    documentName,
    snapshot: { roleLabel, periodLabel, rows },
    stamp,
  });
};

export interface ClearanceRowExport {
  name: string;
  contractor?: string;
  stage?: string;
  start?: string;
  end?: string;
  statusLabel: string;
  approvedBy?: string;
  approvedAt?: string;
  warning?: string;
  phaseName?: string | null;
}

export const generateClearanceWord = async (params: {
  rows: ClearanceRowExport[];
  projectName?: string;
}) => {
  const documentName = "Startbesked arbetsberedningar";
  const stamp = await createStampForCurrentUser({
    docNumber: "24b",
    documentName,
    projectName: params.projectName,
  });

  const children: (Paragraph | Table)[] = [
    p(
      "Sammanställning av arbetsberedningar i projektet och vilka som har fått klart för start av Bas-U."
    ),
    p(`Uttagen: ${new Date().toLocaleString("sv-SE")}`),
    h1("1. Arbetsberedningar"),
    table(
      [
        "Arbetsberedning",
        "Entreprenör",
        "Etapp",
        "Period",
        "Status",
        REVISION_LIST_HEADERS[0],
        REVISION_LIST_HEADERS[1],
        REVISION_LIST_HEADERS[2],
      ],
      params.rows.map((r) => [
        r.name,
        r.contractor ?? "",
        r.stage ?? "",
        [r.start, r.end].filter(Boolean).join(" – "),
        r.statusLabel,
        [r.approvedBy, r.approvedAt].filter(Boolean).join(" ") || "–",
        r.phaseName || currentPhaseName(),
        r.warning ?? "",
      ]),
      { columnWidths: [1700, 1200, 1000, 1300, 1100, 1300, 1100, 660] }
    ),
  ];
  const footer = await documentFooter({ docNumber: "24b", documentType: "startbesked_arbetsberedningar", includeSignatures: false });

  const doc = buildDocument({
    meta: {
      status: footer.status,
      version: footer.version,
      review: footer.review,
      docNumber: "24b", documentName, projectName: params.projectName },
    stamp,
    children: [...children, ...footer.children],
  });
  await downloadWordDocument(doc, "24b_Startbesked_arbetsberedningar.docx", {
    documentType: "startbesked_arbetsberedningar",
    docNumber: "24b",
    documentName,
    snapshot: params.rows,
    stamp,
  });
};

export interface SafetySheetRowExport {
  productName: string;
  supplier?: string;
  usageArea?: string;
  quantity?: string;
  hazardCodes?: string;
  protectiveMeasures?: string;
  contractor?: string;
  workPreparation?: string;
  fileName?: string;
  createdAt?: string;
  changedBy?: string | null;
  phaseName?: string | null;
}

export const generateSafetyDataSheetListWord = async (params: {
  rows: SafetySheetRowExport[];
  projectName?: string;
}) => {
  const documentName = "Kemikalieförteckning och säkerhetsdatablad";
  const stamp = await createStampForCurrentUser({
    documentName,
    projectName: params.projectName,
  });

  const children: (Paragraph | Table)[] = [
    p(
      "Förteckning över kemiska produkter som används på arbetsplatsen, med tillhörande säkerhetsdatablad inlämnade av entreprenörerna."
    ),
    p(`Uttagen: ${new Date().toLocaleString("sv-SE")}`),
    h1("1. Kemikalieförteckning"),
    table(
      [
        "Produkt",
        "Leverantör",
        "Används till",
        "Faromärkning",
        "Skyddsåtgärder",
        "Entreprenör",
        REVISION_LIST_HEADERS[0],
        REVISION_LIST_HEADERS[1],
        "Datablad",
      ],
      params.rows.map((r) => [
        r.productName,
        r.supplier ?? "",
        [r.usageArea, r.quantity].filter(Boolean).join(" · "),
        r.hazardCodes ?? "",
        r.protectiveMeasures ?? "",
        [r.contractor, r.workPreparation].filter(Boolean).join(" / "),
        r.changedBy || "–",
        r.phaseName || currentPhaseName(),
        r.fileName ?? "Saknas",
      ]),
      { columnWidths: [1300, 1000, 1200, 1000, 1400, 1100, 1000, 900, 460] }
    ),
  ];
  const footer = await documentFooter({ docNumber: undefined, documentType: "sakerhetsdatablad", includeSignatures: false });

  const doc = buildDocument({ meta: {
      status: footer.status,
      version: footer.version,
      review: footer.review,
      documentName, projectName: params.projectName }, stamp, children });
  await downloadWordDocument(doc, "Kemikalieforteckning_sakerhetsdatablad.docx", {
    documentType: "sakerhetsdatablad",
    documentName,
    snapshot: params.rows,
    stamp,
  });
};
